/**
 * Track F — save helpers: showSaveFilePicker when available (with
 * suggestedName + type accept), Blob + <a download> fallback.
 */

// Minimal ambient shape for the File System Access API — lib.dom may lack it,
// and declaring it locally (instead of augmenting Window) avoids clashing with
// any version that does define it.
type ShowSaveFilePicker = (options?: {
  suggestedName?: string;
  types?: Array<{ description?: string; accept: Record<string, string[]> }>;
}) => Promise<{
  createWritable(): Promise<{
    write(data: Blob | string | BufferSource): Promise<void>;
    close(): Promise<void>;
  }>;
}>;

function normalizeExtension(extension: string): string {
  const ext = extension.trim();
  if (!ext) return "";
  return ext.charAt(0) === "." ? ext : "." + ext;
}

/**
 * Save a Blob as a file. Prefers the File System Access save dialog; falls back
 * to an anchor-triggered Blob download. Returns false when the user cancels the
 * save dialog (treated as "not saved", not an error). Never throws. This is the
 * shared core used by both text and binary saves — all client-side, no network.
 */
export async function saveBlob(
  blob: Blob,
  suggestedName: string,
  mime: string,
  extension: string,
): Promise<boolean> {
  const ext = normalizeExtension(extension);

  const picker = (globalThis as { showSaveFilePicker?: ShowSaveFilePicker }).showSaveFilePicker;
  if (typeof picker === "function") {
    try {
      const handle = await picker({
        suggestedName,
        types: ext
          ? [{ description: `${ext.slice(1).toUpperCase()} file`, accept: { [mime || "application/octet-stream"]: [ext] } }]
          : undefined,
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return true;
    } catch (err) {
      if (err && typeof err === "object" && (err as { name?: string }).name === "AbortError") {
        return false;
      }
      // Any other failure (unsupported, security) falls through to the anchor path.
    }
  }

  if (typeof document === "undefined") return false;
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = suggestedName;
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
  return true;
}

/**
 * Save raw bytes as a file (e.g. an assembled .zip bundle). Wraps the bytes in
 * a Blob and defers to {@link saveBlob}. Returns false when the user cancels.
 */
export async function saveBinaryFile(
  bytes: Uint8Array | ArrayBuffer,
  suggestedName: string,
  mime: string,
  extension: string,
): Promise<boolean> {
  const blob = new Blob([bytes as BlobPart], { type: mime || "application/octet-stream" });
  return saveBlob(blob, suggestedName, mime, extension);
}

/**
 * Save text as a file. Prefers the File System Access save dialog; falls back
 * to an anchor-triggered Blob download. Returns false when the user cancels the
 * save dialog (treated as "not saved", not an error). Never throws.
 */
export async function saveTextFile(
  text: string,
  suggestedName: string,
  mime: string,
  extension: string,
): Promise<boolean> {
  const blob = new Blob([text], { type: mime || "application/octet-stream" });
  return saveBlob(blob, suggestedName, mime, extension);
}

// Characters reserved on common filesystems, plus whitespace and hyphens, which
// we normalize to underscores. The forward slash is escaped so simple TS
// type-stripping lexers don't misread it as ending the regex literal.
const UNSAFE_CHARS = /[<>:"\/\\|?*\s-]/g;

/**
 * Derive an export filename from a source name + suffix, e.g.
 * deriveFileName("logo.svg", "_pixelated.svg") -> "logo_pixelated.svg".
 * Strips any directory, drops the source extension, and replaces characters
 * that are unsafe in filenames.
 */
export function deriveFileName(sourceName: string, suffix: string): string {
  let name = String(sourceName ?? "");
  // Drop any directory component (either separator style).
  const slash = Math.max(name.lastIndexOf("/"), name.lastIndexOf("\\"));
  if (slash >= 0) name = name.slice(slash + 1);
  // Drop a trailing extension.
  name = name.replace(/\.[^.]+$/, "");
  // Trim leading/trailing whitespace and dots (awkward/invalid on Windows) BEFORE
  // collapsing internal separators, so an all-whitespace stem reduces to empty.
  name = name.replace(/^[\s.]+/, "").replace(/[\s.]+$/, "");
  // Replace reserved characters (and internal spaces/hyphens) with underscores.
  name = name.replace(UNSAFE_CHARS, "_");
  if (!name) name = "export";
  return name + suffix;
}

/**
 * Disambiguate a filename against a set of names already in use, so repeated
 * saves / bundled entries never collide. Splits off the extension and inserts
 * an incrementing `_2`, `_3`, … before it ("logo.svg" -> "logo_2.svg") until
 * the name is unused. Comparison is case-insensitive (safer for Windows/macOS
 * filesystems). The returned name is added to `used` so successive calls with
 * the same set keep producing distinct names. Pure, no network.
 */
export function disambiguateFileName(name: string, used: Set<string>): string {
  const key = (n: string): string => n.toLowerCase();
  if (!used.has(key(name))) {
    used.add(key(name));
    return name;
  }
  // Split "stem" + ".ext" (last dot only; no dot -> whole name is the stem).
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : "";
  for (let i = 2; ; i++) {
    const candidate = `${stem}_${i}${ext}`;
    if (!used.has(key(candidate))) {
      used.add(key(candidate));
      return candidate;
    }
  }
}
